import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UsuarioService } from '../../services/usuario/usuario.service';
import { GrupoDePrivilegioService } from '../../services/usuario/grupo-de-privilegio.service';
import { PessoaService } from '../../services/pessoas/pessoas.service';
import { PessoaDTO, UsuarioCreateDTO, UsuarioDTO, UsuarioFormDTO, UsuarioUpdateDTO } from '../../models/usuario.model';
import { GrupoDePrivilegioDTO } from '../../models/grupo-de-privilegio.model';
import { TableColumn } from '../../models/shared/table-column.model';
import { MessageService, SortEvent } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, interval, Subject, Subscription, switchMap, take } from 'rxjs';
import { PaisDDI } from '../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../shared/data/paises-ddi.data';
import { ContratacaoUsuariosService } from '../../services/usuario/contratacao-usuarios.service';
import { LimiteDeUsuarios, SimulacaoContratacaoUsuarios } from '../../models/planos/plano.model';
import { isEmailValido } from '../../shared/utils/email.util';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';


@Component({
  selector: 'app-usuario',
  templateUrl: './usuario.component.html',
  styleUrls: ['./usuario.component.scss'],
  providers: [MessageService],
  // TODO: migrar para signals + OnPush em refactor maior
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class UsuarioComponent implements OnInit, OnDestroy {
  // Controle de abas (0 = Cadastro, 1 = Lista)
  activeTabIndex: number = 1;

  usuarios: UsuarioDTO[] = [];
  usuariosOriginal: UsuarioDTO[] = [];
  grupoDePrivilegioList: { label: string; value: number }[] = [];
  form: UsuarioFormDTO = this.createEmptyForm();
  filterName = '';
  private search$ = new Subject<string>();

  // Injecao do DestroyRef para takeUntilDestroyed
  private destroyRef = inject(DestroyRef);

  // Paginacao
  first: number = 0;
  rows: number = 10;
  private _lastRows: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Controle de validacao visual
  isFormSubmitted = false;
  submitting = false;

  // Controle de modo edicao inline
  modoEdicao = false;
  usuarioEditandoId: number | null = null;
  pessoaEditandoId: number | null = null;
  pessoaEditandoCompleta: PessoaDTO | null = null;

  // DDI - dropdown de codigo de pais
  paisSelecionado: PaisDDI = PAISES_DDI[0]; // Paraguay (+595) como padrao

  // Controle do modal de exclusao e retorno de foco
  deleteDialogVisible = false;
  deleteItemId: string | null = null;
  private lastTriggerElement: HTMLElement | null = null;

  // Controle do dialogo de alteracao de senha (Divergência 4)
  senhaDialogVisible = false;
  senhaUsuarioId: number | null = null;
  senhaForm = { senhaNova: '', confirmacaoSenha: '' };
  senhaFormSubmitted = false;

  // Controle de ativar/inativar em progresso (Divergência 5)
  statusEmProgresso: Set<number> = new Set();

  // ---------------------------------------------------------------------------
  // Usuários de acesso adicionais (limite do plano e contratação avulsa)
  // ---------------------------------------------------------------------------

  /** Limite vindo do backend. Null enquanto carrega ou se a consulta falhar. */
  limiteUsuarios: LimiteDeUsuarios | null = null;

  contratarDialogVisible = false;
  quantidadeParaContratar = 1;
  readonly MAX_USUARIOS_CONTRATACAO = 100;
  simulacaoContratacao: SimulacaoContratacaoUsuarios | null = null;
  carregandoSimulacao = false;
  gerandoQrContratacao = false;
  gerandoLinkTpagoContratacao = false;
  /** QR em exibição. Enquanto existir, a tela está esperando o pagamento. */
  urlQrContratacao: string | null = null;
  /** Link do cartão já aberto: a tela troca o seletor pela espera da confirmação. */
  aguardandoPagamentoTpago = false;
  private aliasContratacao = '';
  private pollingContratacaoSub: Subscription | null = null;

  /**
   * Empresa contadora parceira. Vem de `getSessao()`, não do flag `contador` do storage — aquele
   * só é gravado no login e fica obsoleto quando o usuário troca de empresa.
   */
  private empresaEhContador = false;

  /** Diálogo que oferece o contato do suporte quando não há caminho de autoatendimento. */
  suporteDialogVisible = false;

  /** Contato do suporte — mesmo número já usado no menu lateral. */
  readonly whatsappSuporte = 'https://wa.me/595993290585';

  // Planos são comercializados sempre em Guarani, independente da moeda da empresa.
  readonly moedaPlano = 'PYG';
  readonly localePlano = 'es-PY';

  texts = {
    titulo: '',
    titulo_edicao: '',
    subtitulo: '',
    aba_cadastro: '',
    aba_lista: '',
    novo_usuario: '',
    dados_do_usuario: '',
    tabela_de_usuarios: '',
    nenhum_usuario: '',
    ajuste_filtros: '',
    carregando: '',
    confirmacao: '',
    confirmar_exclusao: '',
    alterar_senha_titulo: '',
    alterar_senha_subtitulo: '',
    alterar_senha_label_nova: '',
    alterar_senha_label_confirmar: '',
    alterar_senha_placeholder_nova: '',
    alterar_senha_placeholder_confirmar: ''
  };

  labels = {
    nome: '',
    username: '',
    e_mail: '',
    senha: '',
    grupo_de_privilegio: '',
    telefone: '',
    id: '',
    imagem: '',
    foto: '',
    status: '',
    acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    nome_do_usuario: '',
    username: '',
    e_mail: '',
    senha: '',
    selecione_um_grupo: '',
    telefone: '',
    pesquisar: ''
  };

  botoes = {
    adicionar: '',
    salvar: '',
    cancelar_edicao: '',
    confirmar: '',
    cancelar: '',
    fechar_esc: '',
    guardar_ctrl_s: ''
  };

  tooltips = {
    editar: '',
    excluir: '',
    alterar_senha: '',
    ativar: '',
    inativar: ''
  };

  private langChangeSub!: Subscription;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'usuario-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'usuario.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'imagem', labelKey: 'usuario.labels.imagem', visible: true, fixed: false, defaultVisible: true },
    { key: 'username', labelKey: 'usuario.labels.username', visible: true, fixed: false, defaultVisible: true },
    { key: 'email', labelKey: 'usuario.labels.e_mail', visible: true, fixed: false, defaultVisible: true },
    { key: 'grupo', labelKey: 'usuario.labels.grupo_de_privilegio', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'usuario.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  constructor(
    private usuarioService: UsuarioService,
    private grupoDePrivilegioService: GrupoDePrivilegioService,
    private pessoaService: PessoaService,
    private messageService: MessageService,
    private translate: TranslateService,
    private contratacaoUsuariosService: ContratacaoUsuariosService,
    private empresaConfigService: EmpresaConfigService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadGruposDePrivilegio();
    this.findUsuarios();
    this.carregarLimiteDeUsuarios();
    this.carregarSeEhContador();
    this.loadColumnPreferences();
    this.setupSearchDebounce();
    this.loadTranslations();

    this.langChangeSub = this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadTranslations();
      });
  }

  ngOnDestroy(): void {
    this.search$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
    this.pararPollingContratacao();
  }

  // ===========================================================================
  // Usuários de acesso adicionais
  // ===========================================================================

  /**
   * Carrega o limite do plano. Falha aqui não pode travar a tela de usuários: sem o limite,
   * o aviso e o botão de contratar simplesmente não aparecem e a criação segue sendo validada
   * pelo backend.
   */
  private carregarLimiteDeUsuarios(): void {
    this.contratacaoUsuariosService.consultarLimite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (limite) => { this.limiteUsuarios = limite; },
        error: () => { this.limiteUsuarios = null; },
      });
  }

  /**
   * `getSessao()` é cacheado com `shareReplay(1)` e o menu-lateral já o consome na mesma carga,
   * então isto não gera requisição extra. Falha mantém `false` e a tela cai no comportamento
   * anterior (atalho para planos).
   */
  private carregarSeEhContador(): void {
    this.empresaConfigService.getSessao()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (empresa) => { this.empresaEhContador = empresa?.contador === true; },
        error: () => { this.empresaEhContador = false; },
      });
  }

  /** Empresa sem vaga livre: criar usuário deixa de abrir o formulário e vira contratação. */
  get semVagasDisponiveis(): boolean {
    return !!this.limiteUsuarios && !this.limiteUsuarios.podeCriarUsuario;
  }

  /**
   * Contador sem vaga não tem autoatendimento: plano ESPECIAL não vende usuário avulso e a
   * quantidade dele é definida pela Avati na aprovação. O único caminho é o suporte.
   */
  get mostrarContatoSuporte(): boolean {
    return this.semVagasDisponiveis
      && !this.limiteUsuarios?.podeContratarUsuarios
      && this.empresaEhContador;
  }

  abrirDialogSuporte(): void {
    this.suporteDialogVisible = true;
  }

  fecharDialogSuporte(): void {
    this.suporteDialogVisible = false;
  }

  /** Abre a conversa com o suporte. `noopener` evita que a aba aberta acesse esta janela. */
  abrirWhatsappSuporte(): void {
    window.open(this.whatsappSuporte, '_blank', 'noopener');
  }

  /**
   * Fecha a aba "Cadastro" quando não há vaga. Exceto em edição: editar não consome vaga, e sem
   * a ressalva o botão de editar da lista cairia numa aba desabilitada.
   */
  get cadastroBloqueado(): boolean {
    return this.semVagasDisponiveis && !this.modoEdicao;
  }

  /**
   * Mostra o atalho para a tela de planos quando as vagas acabaram e o plano atual não vende
   * usuário avulso (gratuito, ESPECIAL ou vencido) — nesses casos contratar/renovar plano é a
   * única saída, e sem o atalho a tela só diria que acabou.
   *
   * EXCETO para contador: trocar de plano cria uma inscrição nova e as vagas concedidas
   * (`usuariosCortesia`) NÃO são copiadas — ele pagaria e ainda perderia acessos, com os
   * excedentes barrados no login. Para ele o caminho é `mostrarContatoSuporte`.
   */
  get mostrarAtalhoParaPlanos(): boolean {
    return this.semVagasDisponiveis
      && !this.limiteUsuarios?.podeContratarUsuarios
      && !this.empresaEhContador;
  }

  /**
   * O motivo viaja por query param, e não por toast: o `<p-toast>` é local a esta tela e morreria
   * na navegação, deixando o cliente chegar em /planos sem saber por quê.
   */
  irParaPlanos(motivoLimiteUsuarios = false): void {
    this.router.navigate(
      ['/planos'],
      motivoLimiteUsuarios ? { queryParams: { limiteUsuarios: 'true' } } : {}
    );
  }

  /** Alguma cobrança já foi gerada: o seletor de quantidade sai de cena. */
  get aguardandoPagamentoContratacao(): boolean {
    return !!this.urlQrContratacao || this.aguardandoPagamentoTpago;
  }

  /** Evita disparar duas cobranças enquanto uma está sendo criada. */
  get gerandoPagamentoContratacao(): boolean {
    return this.gerandoQrContratacao || this.gerandoLinkTpagoContratacao;
  }

  abrirDialogContratacao(): void {
    // Plano gratuito, ESPECIAL ou vencido não vende usuário avulso (não há período a cobrar).
    // O botão já não aparece nesses casos; a guarda aqui é para o diálogo nunca abrir por um
    // caminho alternativo e deixar o cliente escolhendo quantidade para levar erro no fim.
    if (!this.limiteUsuarios?.podeContratarUsuarios) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('usuario.contratacao.contrate_um_plano'),
      });
      return;
    }

    this.quantidadeParaContratar = 1;
    this.urlQrContratacao = null;
    this.aguardandoPagamentoTpago = false;
    this.aliasContratacao = '';
    this.simulacaoContratacao = null;
    this.contratarDialogVisible = true;
    this.simularContratacao();
  }

  fecharDialogContratacao(): void {
    this.contratarDialogVisible = false;
    this.pararPollingContratacao();
    this.urlQrContratacao = null;
    this.aguardandoPagamentoTpago = false;
    this.aliasContratacao = '';
  }

  alterarQuantidadeParaContratar(delta: number): void {
    const nova = this.quantidadeParaContratar + delta;
    this.quantidadeParaContratar = Math.min(Math.max(nova, 1), this.MAX_USUARIOS_CONTRATACAO);
    this.simularContratacao();
  }

  /** Prévia do valor. Quem calcula é o backend — o front nunca inventa preço. */
  private simularContratacao(): void {
    this.carregandoSimulacao = true;
    this.contratacaoUsuariosService.simular(this.quantidadeParaContratar)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (simulacao) => {
          this.simulacaoContratacao = simulacao;
          this.carregandoSimulacao = false;
        },
        error: (err) => {
          this.simulacaoContratacao = null;
          this.carregandoSimulacao = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: err?.error?.message || this.translate.instant('usuario.contratacao.erro_simular'),
          });
        },
      });
  }

  /**
   * Pagamento por cartão: abre o link do T-Pago em outra aba e fica esperando a confirmação,
   * igual ao "Assinar" da tela de planos. A cobrança é única — o backend cancela o link assim
   * que o pagamento entra, para não virar débito recorrente.
   */
  gerarLinkTpagoContratacao(): void {
    if (this.gerandoPagamentoContratacao) return;
    this.gerandoLinkTpagoContratacao = true;

    this.contratacaoUsuariosService.gerarLinkTpago(this.quantidadeParaContratar)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (intencao) => {
          this.gerandoLinkTpagoContratacao = false;
          if (!intencao.linkUrl || !intencao.linkAlias) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.translate.instant('usuario.contratacao.erro_link'),
            });
            return;
          }
          this.aliasContratacao = intencao.linkAlias;
          this.aguardandoPagamentoTpago = true;
          window.open(intencao.linkUrl, '_blank');
          this.iniciarPollingContratacao();
        },
        error: (err) => {
          this.gerandoLinkTpagoContratacao = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: err?.error?.message || this.translate.instant('usuario.contratacao.erro_link'),
          });
        },
      });
  }

  gerarQrContratacao(): void {
    if (this.gerandoPagamentoContratacao) return;
    this.gerandoQrContratacao = true;

    this.contratacaoUsuariosService.gerarQr(this.quantidadeParaContratar)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (intencao) => {
          this.gerandoQrContratacao = false;
          if (!intencao.urlQr || !intencao.hookAlias) {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro'),
              detail: this.translate.instant('usuario.contratacao.erro_qrcode'),
            });
            return;
          }
          this.urlQrContratacao = intencao.urlQr;
          this.aliasContratacao = intencao.hookAlias;
          this.iniciarPollingContratacao();
        },
        error: (err) => {
          this.gerandoQrContratacao = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: err?.error?.message || this.translate.instant('usuario.contratacao.erro_qrcode'),
          });
        },
      });
  }

  /**
   * Espera a confirmação do pagamento. O backend responde 404 até o callback da Bancard chegar,
   * então erro aqui é "ainda não" — não é falha. Para sozinho depois de 5 minutos para não
   * ficar batendo no servidor com uma aba esquecida aberta.
   */
  private iniciarPollingContratacao(): void {
    this.pararPollingContratacao();
    const alias = this.aliasContratacao;

    this.pollingContratacaoSub = interval(5000)
      .pipe(
        take(60),
        switchMap(() => this.contratacaoUsuariosService.verificarPagamento(alias).pipe(catchError(() => EMPTY))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((contratacao) => {
        if (!contratacao || contratacao.status !== 'PAGA') return;
        this.pararPollingContratacao();
        this.contratarDialogVisible = false;
        this.urlQrContratacao = null;
        this.aguardandoPagamentoTpago = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('usuario.contratacao.pagamento_confirmado'),
        });
        // As vagas já valem: recarrega o limite e a lista.
        this.carregarLimiteDeUsuarios();
        this.findUsuarios();
      });
  }

  private pararPollingContratacao(): void {
    this.pollingContratacaoSub?.unsubscribe();
    this.pollingContratacaoSub = null;
  }

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.subtitulo = this.translate.instant('usuario.texts.subtitulo_lista');
    } else {
      this.texts.subtitulo = this.translate.instant('usuario.texts.subtitulo');
    }
  }

  private loadTranslations(): void {
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      titulo: this.translate.instant('usuario.texts.adicionar_novo_usuario'),
      titulo_edicao: this.translate.instant('usuario.texts.editar_usuario'),
      subtitulo: this.translate.instant(
        isListaAtiva ? 'usuario.texts.subtitulo_lista' : 'usuario.texts.subtitulo'
      ),
      aba_cadastro: this.translate.instant('usuario.texts.aba_cadastro'),
      aba_lista: this.translate.instant('usuario.texts.aba_lista'),
      novo_usuario: this.translate.instant('usuario.texts.novo_usuario'),
      dados_do_usuario: this.translate.instant('usuario.texts.dados_do_usuario'),
      tabela_de_usuarios: this.translate.instant('usuario.texts.tabela_de_usuarios'),
      nenhum_usuario: this.translate.instant('usuario.texts.nenhum_usuario'),
      ajuste_filtros: this.translate.instant('usuario.texts.ajuste_filtros'),
      carregando: this.translate.instant('usuario.texts.carregando'),
      confirmacao: this.translate.instant('common.confirmacao'),
      confirmar_exclusao: this.translate.instant('usuario.mensagens.confirmar_exclusao'),
      alterar_senha_titulo: this.translate.instant('usuario.texts.alterar_senha_titulo'),
      alterar_senha_subtitulo: this.translate.instant('usuario.texts.alterar_senha_subtitulo'),
      alterar_senha_label_nova: this.translate.instant('usuario.labels.senha_nova'),
      alterar_senha_label_confirmar: this.translate.instant('usuario.labels.confirmacao_senha'),
      alterar_senha_placeholder_nova: this.translate.instant('usuario.placeholders.senha_nova'),
      alterar_senha_placeholder_confirmar: this.translate.instant('usuario.placeholders.confirmacao_senha')
    };

    this.labels = {
      nome: this.translate.instant('usuario.labels.nome'),
      username: this.translate.instant('usuario.labels.username'),
      e_mail: this.translate.instant('usuario.labels.e_mail'),
      senha: this.translate.instant('usuario.labels.senha'),
      grupo_de_privilegio: this.translate.instant('usuario.labels.grupo_de_privilegio'),
      telefone: this.translate.instant('usuario.labels.telefone'),
      id: this.translate.instant('usuario.labels.id'),
      imagem: this.translate.instant('usuario.labels.imagem'),
      foto: this.translate.instant('usuario.labels.foto'),
      status: this.translate.instant('usuario.labels.status'),
      acoes: this.translate.instant('usuario.labels.acoes'),
      itens_por_pagina: this.translate.instant('usuario.labels.itens_por_pagina')
    };

    this.placeholders = {
      nome_do_usuario: this.translate.instant('usuario.placeholders.nome_do_usuario'),
      username: this.translate.instant('usuario.placeholders.username'),
      e_mail: this.translate.instant('usuario.placeholders.e_mail'),
      senha: this.translate.instant('usuario.placeholders.senha'),
      selecione_um_grupo: this.translate.instant('usuario.placeholders.selecione_um_grupo'),
      telefone: this.translate.instant('usuario.placeholders.telefone'),
      pesquisar: this.translate.instant('usuario.placeholders.pesquisar')
    };

    this.botoes = {
      adicionar: this.translate.instant('usuario.botoes.adicionar'),
      salvar: this.translate.instant('usuario.botoes.salvar'),
      cancelar_edicao: this.translate.instant('usuario.botoes.cancelar_edicao'),
      confirmar: this.translate.instant('usuario.botoes.confirmar'),
      cancelar: this.translate.instant('usuario.botoes.cancelar'),
      fechar_esc: this.translate.instant('usuario.botoes.fechar_esc'),
      guardar_ctrl_s: this.translate.instant('usuario.botoes.guardar_ctrl_s')
    };

    this.tooltips = {
      editar: this.translate.instant('usuario.tooltips.editar'),
      excluir: this.translate.instant('usuario.tooltips.excluir'),
      alterar_senha: this.translate.instant('usuario.tooltips.alterar_senha'),
      ativar: this.translate.instant('usuario.tooltips.ativar'),
      inativar: this.translate.instant('usuario.tooltips.inativar')
    };
  }

  loadGruposDePrivilegio() {
    this.grupoDePrivilegioService.get('/grupoDePrivilegio')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.grupoDePrivilegioList = response.content.map((grupo: GrupoDePrivilegioDTO) => ({
            label: grupo.nome,
            value: grupo.id!,
          }));
        },
        error: () => {}

      });
  }

  findUsuarios() {
    this.loading = true;
    const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
    const params = { page, size: this.rows, sort: `${this.lastfield},${this.lastSort}` };
    this.usuarioService.get('/usuario', params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.usuariosOriginal = response.content || [];
          this.totalRecords = response.totalElements ?? 0;

          // Filtro local aplicado apenas sobre a página corrente
          if (this.filterName && this.filterName.trim().length > 0) {
            this.filtrarUsuariosLocal(this.filterName.trim());
          } else {
            this.usuarios = [...this.usuariosOriginal];
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        }
      });
  }

  private setupSearchDebounce() {
    this.search$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(term => {
      if (term && term.trim().length > 0) {
        this.filtrarUsuariosLocal(term.trim());
      } else {
        this.usuarios = [...this.usuariosOriginal];
      }
    });
  }

  onSearchChange(value: string) {
    this.filterName = value || '';
    this.search$.next(this.filterName);
  }

  /** Filtra usuarios localmente buscando em todas as colunas visiveis */
  private filtrarUsuariosLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.usuarios = this.usuariosOriginal.filter(usuario => {
      if (this.isColumnVisible('id') && usuario.id != null) {
        if (String(usuario.id).includes(termLower)) return true;
      }
      if (this.isColumnVisible('username') && usuario.username) {
        if (usuario.username.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('email') && usuario.email) {
        if (usuario.email.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('grupo') && usuario.grupoDePrivilegio) {
        const grupoNome = usuario.grupoDePrivilegio.nome || '';
        if (grupoNome.toLowerCase().includes(termLower)) return true;
      }
      if (usuario.pessoa?.nome) {
        if (usuario.pessoa.nome.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });
  }

  submit() {
    if (this.submitting) return;
    if (this.modoEdicao) {
      this.submitEdicao();
    } else {
      this.submitCriacao();
    }
  }

  private submitCriacao(): void {
    this.isFormSubmitted = true;

    if (!this.form.nome || !this.form.email || !this.form.senha) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.campos_obrigatorios'),
      });
      return;
    }

    if (!this.form.grupoDePrivilegio?.value) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.grupo_obrigatorio'),
      });
      return;
    }

    if (!isEmailValido(this.form.email)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.email_invalido') || 'E-mail inválido',
      });
      return;
    }

    if (this.form.senha.length < 8) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.senha_minima') || 'A senha deve ter no mínimo 8 caracteres',
      });
      return;
    }

    const telefoneLimpo = this.form.telefone?.replace(/\D/g, '') || '';

    const body: UsuarioCreateDTO = {
      username: this.form.nome, // Divergência 6: username = nome (alinhado ao mobile)
      nome: this.form.nome,
      emailUsuario: this.form.email,
      senha: this.form.senha,
      grupoDePrivilegio_id: this.form.grupoDePrivilegio?.value ?? undefined,
      ddi: telefoneLimpo ? this.paisSelecionado.ddi.replace('+', '') : undefined,
      telefone: telefoneLimpo || undefined,
    };

    this.submitting = true;
    this.usuarioService.post('/usuario', body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('usuario.mensagens.usuario_criado'),
          });
          this.findUsuarios();
          // A vaga recém-ocupada pode ter sido a última — sem recarregar, o gate não fecha.
          this.carregarLimiteDeUsuarios();
          this.resetarFormulario();
          this.activeTabIndex = 1;
        },
        error: (err) => {
          this.submitting = false;
          // Mostra a mensagem do backend (ex.: limite de usuários do plano atingido). Sem isto
          // o formulário só parava de girar, sem dizer o que aconteceu.
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.erro'),
            detail: err?.error?.message || this.translate.instant('usuario.mensagens.erro_criar_usuario'),
          });
          // O limite pode ter estourado desde o carregamento da tela: recarrega para o botão
          // de contratar aparecer.
          this.carregarLimiteDeUsuarios();
        }
      });
  }

  private submitEdicao(): void {
    this.isFormSubmitted = true;

    if (!this.form.email) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.campos_obrigatorios'),
      });
      return;
    }

    if (!this.form.grupoDePrivilegio?.value) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.grupo_obrigatorio'),
      });
      return;
    }

    if (!isEmailValido(this.form.email)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.email_invalido') || 'E-mail inválido',
      });
      return;
    }

    const telefoneLimpo = this.form.telefone?.replace(/\D/g, '') || '';
    const ddiLimpo = telefoneLimpo ? this.paisSelecionado.ddi.replace('+', '') : undefined;

    const bodyUsuario: UsuarioUpdateDTO = {
      username: this.form.username, // OBRIGATORIO no PUT
      email: this.form.email,
      nome: this.form.nome || undefined,
      grupoDePrivilegio_id: this.form.grupoDePrivilegio?.value ?? undefined,
      ddi: ddiLimpo,
      telefone: telefoneLimpo || undefined,
      imagem: this.form.imagem ?? undefined,
      ativo: this.form.ativo,
    };

    // Helper: normaliza enum que pode vir como objeto { enum: "VALOR", ... } ou string "VALOR"
    const normalizarEnum = (v: any): any =>
      v && typeof v === 'object' && 'enum' in v ? v.enum : v;

    const executarPutUsuario = () =>
      this.usuarioService.put(`/usuario/${this.usuarioEditandoId}`, bodyUsuario)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.submitting = false;
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.sucesso'),
              detail: this.translate.instant('usuario.mensagens.usuario_editado'),
            });
            this.findUsuarios();
            this.resetarFormulario();
            this.activeTabIndex = 1;
          },
          error: () => {
            this.submitting = false;
          }
        });

    this.submitting = true;
    if (this.pessoaEditandoId) {
      // Normalizar enums antes de enviar ao backend — o GET retorna objetos { enum, nome, id }
      // mas o PessoaDTO do Spring espera apenas a string do enum
      const bodyPessoa = {
        ...this.pessoaEditandoCompleta,
        nome: this.form.nome || '',
        email: this.form.email,
        ddi: ddiLimpo,
        telefone: telefoneLimpo || undefined,
        tipoPessoa: normalizarEnum(this.pessoaEditandoCompleta?.tipoPessoa),
        sexo: normalizarEnum(this.pessoaEditandoCompleta?.sexo),
        tipoNaturezaFiscal: normalizarEnum(this.pessoaEditandoCompleta?.tipoNaturezaFiscal),
      };
      this.pessoaService.put(`/pessoas/${this.pessoaEditandoId}`, bodyPessoa as any)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => executarPutUsuario(),
          error: () => {
            this.submitting = false;
          }
        });
    } else {
      executarPutUsuario();
    }
  }

  editarUsuario(id: string) {
    this.usuarioService.getById(`/usuario/${id}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usuario: UsuarioDTO) => {
          // Bug 2: usar usuario.grupoDePrivilegio?.id (nao grupoDePrivilegio_id que nao existe)
          const grupoDaLista = usuario.grupoDePrivilegio?.id
            ? this.grupoDePrivilegioList.find(g => g.value === usuario.grupoDePrivilegio!.id)
            : undefined;

          // Bug 3: ler nome/ddi/telefone de usuario.pessoa (nao da raiz)
          this.form = {
            id: usuario.id,
            nome: usuario.pessoa?.nome || '',
            username: usuario.username || '',
            email: usuario.email || '',
            senha: '', // senha sempre vazia ao editar — alteração via diálogo dedicado
            telefone: usuario.pessoa?.telefone || '',
            ddi: usuario.pessoa?.ddi || '',
            grupoDePrivilegio: grupoDaLista
              ? { label: grupoDaLista.label, value: grupoDaLista.value }
              : undefined,
            imagem: usuario.imagem ?? null,
            ativo: usuario.ativo ?? true,
          };

          // Restaurar DDI selecionado se existir
          if (usuario.pessoa?.ddi) {
            const paisEncontrado = PAISES_DDI.find(p => p.ddi.replace('+', '') === usuario.pessoa!.ddi);
            if (paisEncontrado) {
              this.paisSelecionado = paisEncontrado;
            }
          }

          this.modoEdicao = true;
          this.usuarioEditandoId = Number(id);
          this.pessoaEditandoId = usuario.pessoa?.id ?? null;
          this.pessoaEditandoCompleta = usuario.pessoa ?? null;
          this.activeTabIndex = 0;
        },
        error: () => {}
      });
  }

  novoUsuario(): void {
    // Sem vaga o formulário nem abre: preencher tudo para colher 422 no salvar é o pior desfecho.
    if (this.desviarQuandoSemVaga()) return;
    this.resetarFormulario();
    this.activeTabIndex = 0;
  }

  /**
   * Desvia quem pediu usuário novo sem ter vaga. Devolve `true` quando desviou — o chamador não
   * deve abrir o formulário.
   *
   * Plano que vende usuário avulso → diálogo de contratação, sem sair da tela.
   * Contador (quantidade definida pela Avati) → diálogo com o contato do suporte.
   * Demais casos sem autoatendimento (gratuito ou vencido) → tela de planos.
   */
  private desviarQuandoSemVaga(): boolean {
    if (!this.semVagasDisponiveis) return false;

    if (this.limiteUsuarios?.podeContratarUsuarios) {
      this.abrirDialogContratacao();
    } else if (this.empresaEhContador) {
      // Contador não tem para onde ir sozinho: quem define a quantidade dele é a Avati.
      this.abrirDialogSuporte();
    } else {
      this.irParaPlanos(true);
    }
    return true;
  }

  cancelarEdicao(): void {
    this.resetarFormulario();
    // Sair da edição pode fechar esta aba (empresa sem vaga): ficar numa aba desabilitada trava.
    if (this.cadastroBloqueado) {
      this.activeTabIndex = 1;
    }
  }

  private resetarFormulario(): void {
    this.form = this.createEmptyForm();
    this.paisSelecionado = PAISES_DDI[0];
    this.modoEdicao = false;
    this.usuarioEditandoId = null;
    this.pessoaEditandoId = null;
    this.pessoaEditandoCompleta = null;
    this.isFormSubmitted = false;
  }

  deletarUsuario(id: string, triggerElement?: HTMLElement) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
    // Guardar referencia do elemento que abriu o modal para retornar foco ao fechar
    this.lastTriggerElement = triggerElement || null;
  }

  onDeleteDialogHide(): void {
    // Retornar foco ao elemento que abriu o modal (acessibilidade)
    if (this.lastTriggerElement) {
      this.lastTriggerElement.focus();
      this.lastTriggerElement = null;
    }
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;
    this.usuarioService.delete(`/usuario/${this.deleteItemId}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('usuario.mensagens.apagado_sucesso'),
          });
          this.findUsuarios();
          // Excluir libera vaga: sem recarregar, o cadastro seguiria bloqueado sem motivo.
          this.carregarLimiteDeUsuarios();
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
        error: () => {
          this.deleteDialogVisible = false;
          this.deleteItemId = null;
        },
      });
  }

  // --- Foto do usuário: converte arquivo para base64 ---

  onFotoSelecionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const arquivo = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      // FileReader.result é "data:image/jpeg;base64,AAA..." — remover o prefixo
      const resultado = reader.result as string;
      const base64 = resultado.split(',')[1] ?? resultado;
      this.form.imagem = base64;
    };
    reader.readAsDataURL(arquivo);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    if (ev.ctrlKey && (ev.key === 's' || ev.key === 'S') && this.senhaDialogVisible) {
      ev.preventDefault();
      this.confirmarAlteracaoSenha();
    }
  }

  // --- Divergência 4: Alterar senha ---

  abrirDialogoSenha(usuarioId: number): void {
    this.senhaUsuarioId = usuarioId;
    this.senhaForm = { senhaNova: '', confirmacaoSenha: '' };
    this.senhaFormSubmitted = false;
    this.senhaDialogVisible = true;
  }

  fecharDialogoSenha(): void {
    this.senhaDialogVisible = false;
    this.senhaUsuarioId = null;
    this.senhaForm = { senhaNova: '', confirmacaoSenha: '' };
    this.senhaFormSubmitted = false;
  }

  confirmarAlteracaoSenha(): void {
    this.senhaFormSubmitted = true;

    if (!this.senhaForm.senhaNova || !this.senhaForm.confirmacaoSenha) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.campos_obrigatorios'),
      });
      return;
    }

    if (this.senhaForm.senhaNova.length < 8) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.senha_minima'),
      });
      return;
    }

    if (this.senhaForm.senhaNova !== this.senhaForm.confirmacaoSenha) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('usuario.mensagens.senhas_nao_coincidem'),
      });
      return;
    }

    this.usuarioService.atualizarSenha(this.senhaUsuarioId!, this.senhaForm.senhaNova)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: this.translate.instant('usuario.mensagens.senha_alterada'),
          });
          this.fecharDialogoSenha();
        },
        error: () => {}

      });
  }

  // --- Divergência 5: Ativar/Inativar usuário ---

  alternarStatusUsuario(usuario: UsuarioDTO): void {
    if (!usuario.id || this.statusEmProgresso.has(usuario.id)) return;

    const novoStatus = !usuario.ativo;
    this.statusEmProgresso.add(usuario.id);

    this.usuarioService.ativarOuInativar(usuario.id, novoStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          usuario.ativo = novoStatus;
          // Inativo não ocupa vaga (RN09 do contrato): ativar e inativar mexem no limite.
          this.carregarLimiteDeUsuarios();
          this.statusEmProgresso.delete(usuario.id);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.sucesso'),
            detail: novoStatus
              ? this.translate.instant('usuario.mensagens.usuario_ativado')
              : this.translate.instant('usuario.mensagens.usuario_inativado'),
          });
        },
        error: () => {
          this.statusEmProgresso.delete(usuario.id);
        }
      });
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  onPageChange(event: { first: number; rows: number }): void {
    this.first = event.first;
    this.rows = event.rows;
    this.findUsuarios();
  }

  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.findUsuarios();
  }

  customSort(event: SortEvent): void {
    if (!event.field) return;
    const field = event.field;
    const novoSort = (event.order ?? 1) === 1 ? 'asc' : 'desc';

    // Guarda contra re-emissao: ignora se campo e direcao nao mudaram
    if (field === this.lastfield && novoSort === this.lastSort) {
      return;
    }

    // Atualiza os parametros de ordenacao e dispara nova chamada a API
    // para que o sort seja aplicado sobre todos os registros, nao apenas a pagina corrente
    this.lastfield = field;
    this.lastSort = novoSort;
    this.first = 0; // volta para a primeira pagina ao reordenar
    this.findUsuarios();
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferências de colunas:', e);
    }
  }

  private createEmptyForm(): UsuarioFormDTO {
    return {
      nome: '',
      username: '',
      email: '',
      senha: '',
      grupoDePrivilegio: undefined,
    };
  }
}
